import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './SignupPage.css';

function SignupStep1() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userName: '',
    jumin1: '',
    jumin2: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 주민등록번호 유효성 검사 함수
  const validateJumin = (jumin1, jumin2) => {
    // 입력값 검증
    if (!jumin1 || jumin1.length !== 6) {
      return { valid: false, message: '주민등록번호 앞자리 6자리를 입력하세요.' };
    }
    if (!jumin2 || jumin2.length !== 7) {
      return { valid: false, message: '주민등록번호 뒷자리 7자리를 입력하세요.' };
    }

    // 숫자만 입력되었는지 확인
    if (!/^\d{6}$/.test(jumin1) || !/^\d{7}$/.test(jumin2)) {
      return { valid: false, message: '주민등록번호는 숫자만 입력 가능합니다.' };
    }

    // 생년월일 유효성 검사
    const month = parseInt(jumin1.substring(2, 4));
    const day = parseInt(jumin1.substring(4, 6));

    if (month < 1 || month > 12) {
      return { valid: false, message: '올바른 월(01-12)을 입력하세요.' };
    }
    if (day < 1 || day > 31) {
      return { valid: false, message: '올바른 일(01-31)을 입력하세요.' };
    }

    // 성별코드 검사 (1,2,3,4만 유효)
    const genderCode = parseInt(jumin2.charAt(0));
    if (![1, 2, 3, 4].includes(genderCode)) {
      return { valid: false, message: '주민등록번호 뒷자리 첫 번째 숫자가 올바르지 않습니다.' };
    }

    // 체크섬 검증
    const fullJumin = jumin1 + jumin2;
    const multipliers = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5];
    let sum = 0;

    for (let i = 0; i < 12; i++) {
      sum += parseInt(fullJumin.charAt(i)) * multipliers[i];
    }

    const checkDigit = (11 - (sum % 11)) % 10;
    const lastDigit = parseInt(jumin2.charAt(6));

    if (checkDigit !== lastDigit) {
      return { valid: false, message: '올바르지 않은 주민등록번호입니다.' };
    }

    return { valid: true, message: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 에러 메시지 제거
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 클라이언트 측 유효성 검사
    const newErrors = {};

    if (!formData.userName || formData.userName.length < 2) {
      newErrors.userName = '이름은 2자 이상이어야 합니다.';
    }

    // 주민등록번호 유효성 검사
    const juminValidation = validateJumin(formData.jumin1, formData.jumin2);
    if (!juminValidation.valid) {
      newErrors.jumin = juminValidation.message;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // 서버에 신원 확인 요청
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-identity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userName: formData.userName,
          jumin1: formData.jumin1,
          jumin2: formData.jumin2
        })
      });

      const data = await response.json();

      if (response.ok) {
        // 검증 성공 시 2단계로 이동 (데이터 전달)
        navigate('/signup/step2', {
          state: {
            userName: formData.userName,
            jumin1: formData.jumin1,
            jumin2: formData.jumin2
          }
        });
      } else {
        if (data.error === 'DUPLICATE_JUMIN') {
          setErrors({ jumin: '이미 가입된 주민등록번호입니다.' });
        } else {
          alert(data.error || '신원 확인에 실패했습니다.');
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      alert('신원 확인 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <h1>회원가입 (1/2)</h1>
        <p className="step-description">본인 확인을 위해 이름과 주민등록번호를 입력해주세요.</p>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <label htmlFor="userName">이름 *</label>
            <input
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              placeholder="실명 입력"
              required
            />
            {errors.userName && <span className="error-message">{errors.userName}</span>}
          </div>

          <div className="form-group">
            <label>주민등록번호 *</label>
            <div className="jumin-input-group">
              <input
                type="text"
                name="jumin1"
                value={formData.jumin1}
                onChange={handleChange}
                maxLength="6"
                placeholder="000000"
                required
              />
              <span className="separator">-</span>
              <input
                type="password"
                name="jumin2"
                value={formData.jumin2}
                onChange={handleChange}
                maxLength="7"
                placeholder="0000000"
                required
              />
            </div>
            {errors.jumin && <span className="error-message">{errors.jumin}</span>}
            <p className="info-text">※ 주민등록번호는 중복가입을 막기 위해 사용되며, 관리자가 볼 수 없도록 암호화되어 저장됩니다.</p>
          </div>

          <div className="button-group">
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? '확인 중...' : '다음 단계'}
            </button>
            <button type="button" className="cancel-button" onClick={() => navigate('/login')}>
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignupStep1;
